import { pickBy, range, values } from 'lodash';
import { DateTime } from 'luxon';
import * as z from 'zod';

import { convertStringToDate, CreateLoanRow } from '@willow/types-iso';

import { NUM_BOARDING_INSURANCES } from './createFieldConsts';
import {
  BASE_ESCROW_CITY_FIELDS_FOR_VALIDATION,
  BASE_ESCROW_COUNTY_FIELDS_FOR_VALIDATION,
  BASE_ESCROW_OTHER_FIELDS_FOR_VALIDATION,
  BASE_ESCROW_SCHOOL_FIELDS_FOR_VALIDATION,
  BASE_INSURANCE_FIELDS_FOR_VALIDATION,
  CrossFieldParserContext,
  getParsedFields,
  MULTI_PROP_ESCROW_CITY_FIELDS_FOR_VALIDATION,
  MULTI_PROP_ESCROW_COUNTY_FIELDS_FOR_VALIDATION,
  MULTI_PROP_ESCROW_OTHER_FIELDS_FOR_VALIDATION,
  MULTI_PROP_ESCROW_SCHOOL_FIELDS_FOR_VALIDATION,
  MULTI_PROP_INSURANCE_FIELDS_FOR_VALIDATION,
} from './CreateLoanCrossFieldParser';
import {
  validateAppraisalFields,
  validateAppraisalHasRequiredFieldsOrNone,
} from './crossFieldParsers/validateAppraisalFields';
import { validateAuthorizedContactFields } from './crossFieldParsers/validateAuthorizedContactFields';
import { validateBorrowerFields } from './crossFieldParsers/validateBorrowerFields';
import { validateFeeFields } from './crossFieldParsers/validateFeeFields';

const firstCollectedDateIsInPast = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  const { firstCollectedPaymentDate } = data;

  if (!firstCollectedPaymentDate) {
    return;
  }

  if (convertStringToDate(firstCollectedPaymentDate) < new Date()) {
    ctx.addIssue({
      code: 'custom',
      message: 'Because `firstCollectedPaymentDate` is in the past, the loan will be marked as past due.',
      path: ['firstCollectedPaymentDate'],
    });
  }
};

const stopEmailOrPayments = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  const { stopEmails, stopPayments } = data;

  if (stopEmails) {
    ctx.addIssue({
      code: 'custom',
      message: 'Because `stopEmails` is set to true, the loan will be automatically flagged.',
      path: ['stopEmails'],
    });
  }

  if (stopPayments) {
    ctx.addIssue({
      code: 'custom',
      message: 'Because `stopPayments` is set to true, the loan will be automatically flagged.',
      path: ['stopPayments'],
    });
  }
};

const agedLoanHasBeginningOfYearPrincipalBalance = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  const { acquisitionDate, fundingDate, firstCollectedPaymentDate, beginningOfYearPrincipalBalance, ytdInterestPaid } =
    data;

  if (firstCollectedPaymentDate) {
    const lenderOwnershipStartDate = acquisitionDate || fundingDate;
    const lenderTookOwnershipInYearPriorToWillowServicing =
      DateTime.fromJSDate(convertStringToDate(lenderOwnershipStartDate)).year <
      DateTime.fromJSDate(convertStringToDate(firstCollectedPaymentDate)).year;

    // Lender has been servicing this loan since a previous year, so we need to know the starting year principal balance for tax reporting
    if (lenderTookOwnershipInYearPriorToWillowServicing && !beginningOfYearPrincipalBalance) {
      ctx.addIssue({
        code: 'custom',
        message:
          'Loan that has been active since before start of boarding year should include `beginningOfYearPrincipalBalance` for tax reporting.',
      });
    }

    if (lenderTookOwnershipInYearPriorToWillowServicing && !ytdInterestPaid) {
      ctx.addIssue({
        code: 'custom',
        message:
          'Loan that has been active since before start of boarding year should include `ytdInterestPaid` for tax reporting.',
      });
    }
  }
};

const escrowInsuranceHasPolicyDates = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  for (const n of range(1, NUM_BOARDING_INSURANCES + 1)) {
    if (
      // @ts-ignore
      data[`insurance${n}IsEscrowed`] === true ||
      // @ts-ignore
      (data[`insurance${n}Amount`] != null && data[`insurance${n}Amount`] > 0) ||
      // @ts-ignore
      data[`insurance${n}CompanyName`] != null ||
      // @ts-ignore
      data[`insurance${n}PolicyNumber`] != null
    ) {
      const missingFields: string[] = [];

      for (const key of [`insurance${n}PolicyEnd`, `insurance${n}PolicyNumber`]) {
        // @ts-ignore
        if (data[key] == null) {
          missingFields.push(key);
        }
      }

      if (missingFields.length) {
        const missingFieldsString = missingFields.map((key) => `\`${String(key)}\``).join(', ');
        ctx.addIssue({
          code: 'custom',
          message: `Insurance policy ${n} is missing ${
            missingFields.length > 1 ? 'fields' : 'a field'
          } and will not be added to the loan: ${missingFieldsString}`,
        });
      }
    }
  }
};

const escrowCompaniesHavePolicyNumber = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  for (const n of range(1, 4)) {
    // @ts-ignore
    if (data[`escrow${n}CompanyName`] != null && data[`escrow${n}PolicyNumber`] == null) {
      ctx.addIssue({
        code: 'custom',
        message: `Escrow company ${n} is missing \`escrow${n}PolicyNumber\` and will not be added to the loan`,
      });
    }
  }
};

const validateAcquisitionFields = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  const { correspondentLender, correspondentLenderPhone, correspondentLenderEmail } = data;
  if (correspondentLenderPhone && !correspondentLender) {
    ctx.addIssue({
      code: 'custom',
      path: ['correspondentLenderPhone'],
      message: 'correspondentLenderPhone must be defined to board loan with correspondent data',
    });
  }
  if (correspondentLenderEmail && !correspondentLender) {
    ctx.addIssue({
      code: 'custom',
      path: ['correspondentLenderEmail'],
      message: 'correspondentLenderEmail must be defined to board loan with correspondent data',
    });
  }
};

const hasMonthlyMortgageInsuranceField =
  (context: CrossFieldParserContext) => (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
    const parsedEscrowLines =
      context === 'api'
        ? getParsedFields(data, [
            ...MULTI_PROP_ESCROW_COUNTY_FIELDS_FOR_VALIDATION,
            ...MULTI_PROP_ESCROW_CITY_FIELDS_FOR_VALIDATION,
            ...MULTI_PROP_ESCROW_SCHOOL_FIELDS_FOR_VALIDATION,
            ...MULTI_PROP_ESCROW_OTHER_FIELDS_FOR_VALIDATION,
            ...MULTI_PROP_INSURANCE_FIELDS_FOR_VALIDATION,
          ])
        : getParsedFields(data, [
            ...BASE_ESCROW_COUNTY_FIELDS_FOR_VALIDATION,
            ...BASE_ESCROW_CITY_FIELDS_FOR_VALIDATION,
            ...BASE_ESCROW_SCHOOL_FIELDS_FOR_VALIDATION,
            ...BASE_ESCROW_OTHER_FIELDS_FOR_VALIDATION,
            ...BASE_INSURANCE_FIELDS_FOR_VALIDATION,
          ]);

    const parsedFields = getParsedFields(data, ['monthlyPaymentMi', 'isEscrowed', 'monthlyPaymentEscrow', 'miAmount']);

    if (!parsedEscrowLines || !parsedFields) {
      return;
    }

    const isEscrowedFields = pickBy(parsedEscrowLines, (_, key) => key.toLowerCase().includes('isescrowed'));

    const hasEscrowLines = values(isEscrowedFields).some((v) => v != null);
    const isEscrowed =
      values(isEscrowedFields).some((v) => v === true) ||
      parsedFields.isEscrowed === true ||
      !!parsedFields.monthlyPaymentEscrow;

    if (hasEscrowLines && isEscrowed && parsedFields.monthlyPaymentMi == null && parsedFields.miAmount == null) {
      ctx.addIssue({
        code: 'custom',
        message:
          'Escrowed loan should include `miAmount` or `monthlyPaymentMi` (deprecated) (mortgage insurance). It is currently missing, so it will default to 0.',
      });
    }
  };

// Mirrors the blocking findings in createGinnieMaeRFSMonthlyReport (WILLOW-RFS-LOANID01
// and WILLOW-RFS-LT01) so data problems surface at board time instead of at the GNMA
// reporting deadline. A missing investorLoanId and GNMA-like ownerId values are already
// hard errors in validateInvestorFields, so this only covers what passes those checks:
// a present but malformed Unique Loan ID, and a product type with no GNMA mapping.
const GNMA_ELIGIBLE_PRODUCT_TYPES: ReadonlySet<string> = new Set(['fha', 'va', 'usda', 'pih']);

const ginnieMaeReportingFields = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  const { investorName, investorLoanId, productType } = data;
  if (investorName !== 'GNMA') {
    return;
  }

  if (investorLoanId && !/^\d{1,9}$/.test(investorLoanId)) {
    ctx.addIssue({
      code: 'custom',
      path: ['investorLoanId'],
      message:
        'For Ginnie Mae loans, `investorLoanId` should be the numeric Ginnie Mae Unique Loan ID (up to 9 digits). The current value cannot be reported in GNMA RFS reporting.',
    });
  }

  if (!productType) {
    ctx.addIssue({
      code: 'custom',
      path: ['productType'],
      message:
        'Ginnie Mae loans should include `productType` (fha, va, usda, or pih). Without it, the loan cannot be included in GNMA RFS reporting.',
    });
  } else if (!GNMA_ELIGIBLE_PRODUCT_TYPES.has(productType)) {
    ctx.addIssue({
      code: 'custom',
      path: ['productType'],
      message: `Product type \`${productType}\` does not map to a Ginnie Mae loan type (fha, va, usda, or pih), so the loan cannot be included in GNMA RFS reporting.`,
    });
  }
};

const interestOnlyFields = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  const { interestOnlyFlag, interestOnlyTermMonths, interestOnlyTermYears, interestOnlyEndDate, interestType } = data;
  if (!interestOnlyFlag && interestType) {
    ctx.addIssue({
      code: 'custom',
      message:
        'Interest type is only supported for interest only loans. This field will be ignored and the loan will use the conventional 30-360 interest calculator.',
      path: ['interestType'],
    });
  }

  if (!interestOnlyFlag) {
    for (const [field, value] of [
      ['interestOnlyTermMonths', interestOnlyTermMonths],
      ['interestOnlyTermYears', interestOnlyTermYears],
      ['interestOnlyEndDate', interestOnlyEndDate],
    ] as const) {
      if (value != null) {
        ctx.addIssue({
          code: 'custom',
          message: 'Interest only flag is required for interest only loans.',
          path: [field],
        });
        break;
      }
    }
  }
};

export const getCreateLoanWarningParser = (context: CrossFieldParserContext) =>
  z
    .any()
    .superRefine(firstCollectedDateIsInPast)
    .superRefine(stopEmailOrPayments)
    .superRefine(validateFeeFields)
    .superRefine(agedLoanHasBeginningOfYearPrincipalBalance)
    .superRefine(escrowInsuranceHasPolicyDates)
    .superRefine(escrowCompaniesHavePolicyNumber)
    .superRefine(validateBorrowerFields)
    .superRefine(validateAuthorizedContactFields)
    .superRefine(validateAcquisitionFields)
    .superRefine(hasMonthlyMortgageInsuranceField(context))
    .superRefine(validateAppraisalFields([validateAppraisalHasRequiredFieldsOrNone]))
    .superRefine(interestOnlyFields)
    .superRefine(ginnieMaeReportingFields);
